import React, { useMemo } from 'react';
import { Activity, Award, CalendarDays, CheckCircle2, ClipboardCheck, FileSearch, ShieldCheck } from 'lucide-react';
import { AUDIT_PIPELINE_STAGES, buildAuditPipelineGradient } from '../../utils/auditPipelineUtils';
import './AuditPipelineOverview.css';

const ICONS = {
  'Application Review': FileSearch,
  Scheduling: CalendarDays,
  Audit: ClipboardCheck,
  'Technical Review': ShieldCheck,
  Certificate: Award,
  Completed: CheckCircle2,
};

const AuditPipelineOverview = ({ counts = {}, total = 0, loading = false, onStageClick, selectedStage, children }) => {
  const gradient = useMemo(() => buildAuditPipelineGradient(counts, total), [counts, total]);

  return (
    <section className="audit-pipeline-overview card border-0 shadow-sm h-100" aria-label="Audit pipeline overview">
      <div className="audit-pipeline-overview__header">
        <div>
          <div className="audit-pipeline-overview__eyebrow"><Activity size={14} /> Management command center</div>
          <h3>Live Audit Process</h3>
          <p>Pipeline position, operational workload, portfolio composition, and risk signals in one connected view.</p>
        </div>
        {!loading && <span className="audit-pipeline-overview__total-badge">{total} projects</span>}
      </div>

      <div className="audit-pipeline-overview__body">
        <div className="audit-pipeline-overview__distribution">
          {loading ? (
            <div className="audit-pipeline-overview__ring audit-pipeline-overview__ring--loading" />
          ) : (
            <div className="audit-pipeline-overview__ring" style={{ background: gradient }}>
              <div className="audit-pipeline-overview__ring-center">
                <strong>{total}</strong>
                <span>Total projects</span>
              </div>
            </div>
          )}
          <div className="audit-pipeline-overview__distribution-copy">
            <strong>Stage distribution</strong>
            <span>Each project is counted once at its furthest active workflow stage.</span>
          </div>
        </div>

        <div className="audit-pipeline-overview__stages">
          {AUDIT_PIPELINE_STAGES.map((stage) => {
            const value = Number(counts?.[stage.key]) || 0;
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            const Icon = ICONS[stage.key];
            const content = (
              <>
                <span className="audit-pipeline-overview__stage-icon" style={{ color: stage.color, background: stage.tint }}><Icon size={16} /></span>
                <span className="audit-pipeline-overview__stage-main">
                  <span className="audit-pipeline-overview__stage-label">
                    <strong>{stage.key}</strong>
                    <span style={{ color: stage.color }}>{loading ? '—' : value} <small>({loading ? '—' : `${percentage}%`})</small></span>
                  </span>
                  <span className="audit-pipeline-overview__track">
                    <span style={{ width: loading ? '0%' : `${percentage}%`, background: stage.color }} />
                  </span>
                </span>
              </>
            );

            return onStageClick ? (
              <button
                key={stage.key}
                type="button"
                className={`audit-pipeline-overview__stage${selectedStage === stage.key ? ' is-selected' : ''}`}
                onClick={() => onStageClick(stage.key)}
                aria-pressed={selectedStage === stage.key}
              >
                {content}
              </button>
            ) : (
              <div key={stage.key} className="audit-pipeline-overview__stage">{content}</div>
            );
          })}
        </div>
      </div>
      {children}
    </section>
  );
};

export default AuditPipelineOverview;
